import { useState, useEffect, useRef } from "react";
import { CITIES } from './cities';

// ─── helpers ────────────────────────────────────────────────────────────────

function getTimeInZone(timezone) {
  return new Date().toLocaleTimeString("en-US", {
    timeZone: timezone, hour: "2-digit", minute: "2-digit",
    second: "2-digit", hour12: true,
  });
}

function getDateInZone(timezone) {
  return new Date().toLocaleDateString("en-US", {
    timeZone: timezone, weekday: "long", year: "numeric",
    month: "long", day: "numeric",
  });
}

function getOffsetLabel(timezone, refDate) {
  const now = refDate || new Date();
  const utc = new Date(now.toLocaleString("en-US", { timeZone: "UTC" }));
  const tz  = new Date(now.toLocaleString("en-US", { timeZone: timezone }));
  const hrs = (tz - utc) / 3_600_000;
  const sign = hrs >= 0 ? "+" : "-";
  const abs  = Math.abs(hrs);
  const h    = Math.floor(abs);
  const m    = Math.round((abs - h) * 60);
  return `UTC${sign}${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
}

function getHourFromTZ(timezone) {
  return parseInt(new Date().toLocaleString("en-US", {
    timeZone: timezone, hour: "numeric", hour12: false,
  }));
}

function getDayPhase(hour) {
  if (hour >= 5  && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 21) return "evening";
  return "night";
}

/** Convert an arbitrary date/time from srcTZ → dstTZ */
function convertDateTime(dateStr, timeStr, srcTZ, dstTZ) {
  // Build an ISO-ish string then interpret it as srcTZ
  // e.g. "2025-08-07T14:30:00" in "Asia/Manila"
  const iso = `${dateStr}T${timeStr}:00`;
  // Trick: get the UTC ms by formatting the target string in srcTZ offset
  const srcDate = new Date(
    new Date(iso).toLocaleString("en-US", { timeZone: "UTC" })
  );
  const srcOffset = (() => {
    const ref = new Date(iso + "Z"); // treat as UTC momentarily
    const local = new Date(ref.toLocaleString("en-US", { timeZone: srcTZ }));
    return (ref - local); // ms difference
  })();
  const utcMs = new Date(iso).getTime() + srcOffset;
  const utcDate = new Date(utcMs);

  const resultTime = utcDate.toLocaleTimeString("en-US", {
    timeZone: dstTZ, hour: "2-digit", minute: "2-digit", hour12: true,
  });
  const resultDate = utcDate.toLocaleDateString("en-US", {
    timeZone: dstTZ, weekday: "long", year: "numeric",
    month: "long", day: "numeric",
  });
  const offset = getOffsetLabel(dstTZ, utcDate);
  const phase  = getDayPhase(parseInt(
    utcDate.toLocaleString("en-US", { timeZone: dstTZ, hour: "numeric", hour12: false })
  ));
  return { resultTime, resultDate, offset, phase };
}

// ─── icons ───────────────────────────────────────────────────────────────────

const PHASE_COLORS = {
  morning: "#f0c060", afternoon: "#f0a840", evening: "#c07880", night: "#7288AE",
};

function PhaseIcon({ phase, size = 13 }) {
  const c = "currentColor";
  if (phase === "morning") return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="4" fill={c} opacity="0.9"/>
      {[[12,2,12,5],[12,19,12,22],[2,12,5,12],[19,12,22,12],
        [4.93,4.93,7.05,7.05],[16.95,16.95,19.07,19.07],
        [4.93,19.07,7.05,16.95],[16.95,7.05,19.07,4.93]
      ].map(([x1,y1,x2,y2],i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={c} strokeWidth="2" strokeLinecap="round"/>
      ))}
    </svg>
  );
  if (phase === "afternoon") return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="5" fill={c} opacity="0.9"/>
      {[[12,1,12,4],[12,20,12,23],[1,12,4,12],[20,12,23,12],
        [4.22,4.22,6.34,6.34],[17.66,17.66,19.78,19.78],
        [4.22,19.78,6.34,17.66],[17.66,6.34,19.78,4.22]
      ].map(([x1,y1,x2,y2],i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={c} strokeWidth="2.5" strokeLinecap="round"/>
      ))}
    </svg>
  );
  if (phase === "evening") return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" fill={c} opacity="0.85"/>
    </svg>
  );
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" fill={c} opacity="0.7"/>
      <circle cx="17" cy="5" r="1" fill={c} opacity="0.5"/>
      <circle cx="20" cy="9" r="0.7" fill={c} opacity="0.4"/>
      <circle cx="15" cy="2" r="0.7" fill={c} opacity="0.4"/>
    </svg>
  );
}

// ─── CityDropdown ─────────────────────────────────────────────────────────────

function CityDropdown({ value, onChange, exclude }) {
  const [query, setQuery] = useState("");
  const [open, setOpen]   = useState(false);
  const ref      = useRef(null);
  const inputRef = useRef(null);

  const filtered = CITIES.filter(c =>
    c.name !== exclude && c.name.toLowerCase().includes(query.toLowerCase())
  );
  const selected = CITIES.find(c => c.name === value);

  useEffect(() => {
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <div
        onClick={() => { setOpen(o => !o); setQuery(""); setTimeout(() => inputRef.current?.focus(), 50); }}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "13px 16px",
          background: "rgba(75,86,148,0.18)",
          border: `1.5px solid ${open ? "rgba(114,136,174,0.7)" : "rgba(114,136,174,0.3)"}`,
          borderRadius: 12, cursor: "pointer",
          transition: "border-color 0.2s",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ opacity: 0.45, flexShrink: 0 }}>
          <circle cx="12" cy="10" r="4" stroke="#EAE0CF" strokeWidth="2"/>
          <path d="M12 2C7.03 2 3 6.03 3 10c0 5.25 7.5 12 9 12s9-6.75 9-12c0-3.97-3.03-7-6-8z" stroke="#EAE0CF" strokeWidth="2" fill="none"/>
        </svg>
        <span style={{ color: selected ? "#EAE0CF" : "rgba(234,224,207,0.35)", fontSize: 15, fontWeight: selected ? 500 : 400, flex: 1 }}>
          {selected ? selected.name : "Select a city"}
        </span>
        {selected && <span style={{ fontSize: 11, color: "rgba(234,224,207,0.4)", letterSpacing:"0.05em" }}>{selected.country}</span>}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s", opacity: 0.35, flexShrink:0 }}>
          <path d="M6 9l6 6 6-6" stroke="#EAE0CF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
          background: "#19205a", border: "1.5px solid rgba(114,136,174,0.4)",
          borderRadius: 14, zIndex: 100, overflow: "hidden",
          boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
          animation: "dropIn 0.15s ease",
        }}>
          <div style={{ padding: "9px 10px", borderBottom: "1px solid rgba(114,136,174,0.18)" }}>
            <input
              ref={inputRef} value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search city…"
              style={{
                width: "100%", background: "rgba(75,86,148,0.2)",
                border: "1px solid rgba(114,136,174,0.3)", borderRadius: 8,
                padding: "7px 12px", color: "#EAE0CF", fontSize: 13,
                outline: "none", fontFamily: "DM Sans, sans-serif", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {filtered.length === 0
              ? <div style={{ padding: "14px", color: "rgba(234,224,207,0.35)", fontSize: 13, textAlign: "center" }}>No cities found</div>
              : filtered.map(city => (
                <div
                  key={city.name}
                  onClick={() => { onChange(city.name); setOpen(false); setQuery(""); }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 16px", cursor: "pointer",
                    color: city.name === value ? "#EAE0CF" : "rgba(234,224,207,0.72)",
                    background: city.name === value ? "rgba(75,86,148,0.35)" : "transparent",
                    fontSize: 13, transition: "background 0.12s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(75,86,148,0.25)"}
                  onMouseLeave={e => e.currentTarget.style.background = city.name === value ? "rgba(75,86,148,0.35)" : "transparent"}
                >
                  <span>{city.name}</span>
                  <span style={{ fontSize: 10, opacity: 0.4, letterSpacing: "0.06em" }}>{city.country}</span>
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Live TimeDisplay card ────────────────────────────────────────────────────

function TimeDisplay({ city, primary }) {
  const [time, setTime] = useState("");
  const [date, setDate] = useState("");
  const cityData = CITIES.find(c => c.name === city);

  useEffect(() => {
    if (!cityData) return;
    const tick = () => { setTime(getTimeInZone(cityData.timezone)); setDate(getDateInZone(cityData.timezone)); };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [cityData]);

  if (!cityData || !time) return null;
  const hour   = getHourFromTZ(cityData.timezone);
  const phase  = getDayPhase(hour);
  const offset = getOffsetLabel(cityData.timezone);
  const [h, rest] = time.split(/:(.+)/);

  return (
    <TimeCard city={city} country={cityData.country} primary={primary}
      timeH={h} timeRest={rest} date={date} phase={phase} offset={offset} />
  );
}

// ─── Converted TimeDisplay card ───────────────────────────────────────────────

function ConvertedDisplay({ city, result }) {
  if (!result) return null;
  const cityData = CITIES.find(c => c.name === city);
  const [h, rest] = result.resultTime.split(/:(.+)/);
  return (
    <TimeCard city={city} country={cityData?.country} primary={false}
      timeH={h} timeRest={rest} date={result.resultDate}
      phase={result.phase} offset={result.offset} converted />
  );
}

// ─── Shared card shell ────────────────────────────────────────────────────────

function TimeCard({ city, country, primary, timeH, timeRest, date, phase, offset, converted }) {
  const pc = PHASE_COLORS[phase];
  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column", gap: 8,
      padding: primary ? "28px 24px" : "22px 20px",
      background: primary ? "rgba(75,86,148,0.22)" : "rgba(75,86,148,0.10)",
      borderRadius: 18,
      border: `1.5px solid ${primary ? "rgba(114,136,174,0.45)" : "rgba(114,136,174,0.2)"}`,
      position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: -36, right: -36, width: 110, height: 110, borderRadius: "50%",
        background: `radial-gradient(circle, ${primary ? pc+"28" : "rgba(114,136,174,0.12)"} 0%, transparent 70%)`,
        pointerEvents: "none",
      }}/>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: "0.1em", color: "rgba(234,224,207,0.4)", textTransform: "uppercase", marginBottom: 3 }}>
            {converted ? "Converted →" : primary ? "From" : "To"}
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, color: "#EAE0CF", letterSpacing: "-0.01em" }}>{city}</div>
        </div>
        <div style={{
          display: "flex", alignItems: "center", gap: 5, color: pc,
          fontSize: 11, fontWeight: 500,
          background: `${pc}18`, padding: "4px 9px", borderRadius: 20,
          border: `1px solid ${pc}30`,
        }}>
          <PhaseIcon phase={phase} size={12}/>
          <span style={{ textTransform: "capitalize" }}>{phase}</span>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginTop: 4 }}>
        <span style={{
          fontSize: primary ? 48 : 40, fontWeight: 700, color: "#EAE0CF",
          letterSpacing: "-0.03em", lineHeight: 1, fontVariantNumeric: "tabular-nums",
        }}>{timeH}</span>
        <span style={{
          fontSize: primary ? 26 : 22, fontWeight: 400, color: "rgba(234,224,207,0.5)",
          letterSpacing: "-0.01em", lineHeight: 1, fontVariantNumeric: "tabular-nums",
        }}>:{timeRest}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 2 }}>
        <span style={{ fontSize: 11, color: "rgba(234,224,207,0.4)" }}>{date}</span>
        <span style={{
          fontSize: 10, color: "rgba(234,224,207,0.32)",
          background: "rgba(75,86,148,0.3)", padding: "3px 7px",
          borderRadius: 6, letterSpacing: "0.05em", fontWeight: 500,
        }}>{offset}</span>
      </div>
    </div>
  );
}

// ─── Time diff pill ──────────────────────────────────────────────────────────

function TimeDiff({ city1, city2 }) {
  const c1 = CITIES.find(c => c.name === city1);
  const c2 = CITIES.find(c => c.name === city2);
  if (!c1 || !c2) return null;
  const getOff = tz => {
    const now = new Date();
    const u = new Date(now.toLocaleString("en-US", { timeZone: "UTC" }));
    const l = new Date(now.toLocaleString("en-US", { timeZone: tz }));
    return (l - u) / 3_600_000;
  };
  const diff = getOff(c2.timezone) - getOff(c1.timezone);
  if (diff === 0) return (
    <div style={{ textAlign:"center", fontSize:12, color:"rgba(234,224,207,0.45)" }}>Same timezone</div>
  );
  const sign = diff > 0 ? "+" : "-";
  const abs  = Math.abs(diff);
  const h    = Math.floor(abs);
  const m    = Math.round((abs - h) * 60);
  const lbl  = m > 0 ? `${h}h ${m}m` : `${h}h`;
  return (
    <div style={{ textAlign:"center", fontSize:12, color:"rgba(234,224,207,0.45)" }}>
      {city2} is{" "}
      <span style={{ color: diff > 0 ? "#a8d8a8" : "#f0a0a0", fontWeight: 600, fontSize: 13 }}>
        {sign}{lbl}
      </span>{" "}
      {diff > 0 ? "ahead of" : "behind"} {city1}
    </div>
  );
}

// ─── Custom Converter Panel ───────────────────────────────────────────────────

function CustomConverter({ city1, city2 }) {
  const today = new Date().toISOString().slice(0, 10);
  const nowTime = new Date().toLocaleTimeString("en-US", {
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).replace(/^24/, "00");

  const [inputDate, setInputDate] = useState(today);
  const [inputTime, setInputTime] = useState(nowTime.slice(0,5));
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState(null);

  const c1 = CITIES.find(c => c.name === city1);
  const c2 = CITIES.find(c => c.name === city2);

  useEffect(() => {
    setResult(null);
    setError(null);
  }, [city1, city2]);

  const handleConvert = () => {
    if (!c1 || !c2) { setError("Select both cities first."); return; }
    if (!inputDate || !inputTime) { setError("Enter a valid date and time."); return; }
    try {
      const r = convertDateTime(inputDate, inputTime, c1.timezone, c2.timezone);
      setResult(r);
      setError(null);
    } catch(e) {
      setError("Conversion failed. Check your inputs.");
    }
  };

  // inputStyle moved to CSS class .dt-input for mobile responsiveness

  return (
    <div style={{
      background: "rgba(17,24,68,0.5)",
      border: "1.5px solid rgba(114,136,174,0.2)",
      borderRadius: 18, padding: "20px",
      display: "flex", flexDirection: "column", gap: 14,
    }}>
      {/* Section header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{
          width: 26, height: 26, borderRadius: 8,
          background: "rgba(75,86,148,0.35)",
          border: "1px solid rgba(114,136,174,0.3)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="4" width="18" height="18" rx="3" stroke="#EAE0CF" strokeWidth="2"/>
            <line x1="3" y1="9" x2="21" y2="9" stroke="#EAE0CF" strokeWidth="2"/>
            <line x1="8" y1="2" x2="8" y2="6" stroke="#EAE0CF" strokeWidth="2" strokeLinecap="round"/>
            <line x1="16" y1="2" x2="16" y2="6" stroke="#EAE0CF" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#EAE0CF" }}>Custom Date & Time</div>
          <div style={{ fontSize: 11, color: "rgba(234,224,207,0.4)" }}>
            Convert a specific moment from {city1 || "origin"} → {city2 || "destination"}
          </div>
        </div>
      </div>

      {/* Inputs row */}
      <div style={{ display: "flex", flexDirection: "row", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flexShrink: 0 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.08em", color: "rgba(234,224,207,0.38)", textTransform: "uppercase", marginBottom: 6 }}>Date</div>
          <input
            type="date"
            value={inputDate}
            onChange={e => { setInputDate(e.target.value); setResult(null); }}
            className="dt-input"
            style={{ width: "auto" }}
          />
        </div>
        <div style={{ flexShrink: 0 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.08em", color: "rgba(234,224,207,0.38)", textTransform: "uppercase", marginBottom: 6 }}>Time</div>
          <input
            type="time"
            value={inputTime}
            onChange={e => { setInputTime(e.target.value); setResult(null); }}
            className="dt-input"
            style={{ width: "auto" }}
          />
        </div>
      </div>

      {/* Convert button */}
      <button
        onClick={handleConvert}
        style={{
          width: "100%", padding: "12px",
          background: c1 && c2 ? "rgba(75,86,148,0.55)" : "rgba(75,86,148,0.2)",
          border: "1.5px solid rgba(114,136,174,0.4)",
          borderRadius: 11, color: c1 && c2 ? "#EAE0CF" : "rgba(234,224,207,0.35)",
          fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif",
          cursor: c1 && c2 ? "pointer" : "not-allowed",
          transition: "background 0.2s, transform 0.15s",
          letterSpacing: "0.02em",
        }}
        onMouseEnter={e => { if (c1 && c2) e.currentTarget.style.background = "rgba(75,86,148,0.75)"; }}
        onMouseLeave={e => { if (c1 && c2) e.currentTarget.style.background = "rgba(75,86,148,0.55)"; }}
        onMouseDown={e => { if (c1 && c2) e.currentTarget.style.transform = "scale(0.97)"; }}
        onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
      >
        Convert →
      </button>

      {/* Error */}
      {error && (
        <div style={{
          fontSize: 12, color: "#f0a0a0",
          background: "rgba(240,100,100,0.1)",
          border: "1px solid rgba(240,100,100,0.2)",
          borderRadius: 8, padding: "9px 12px", textAlign: "center",
        }}>{error}</div>
      )}

      {/* Result */}
      {result && (
        <div style={{ animation: "fadeUp 0.25s ease" }}>
          <ConvertedDisplay city={city2} result={result} />
        </div>
      )}
    </div>
  );
}

// ─── Root ────────────────────────────────────────────────────────────────────

export default function Timely() {
  const [city1, setCity1] = useState("Manila");
  const [city2, setCity2] = useState("Seoul");

  const handleSwap = () => { setCity1(city2); setCity2(city1); };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', sans-serif; background: #111844; min-height: 100vh; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(114,136,174,0.3); border-radius: 4px; }
        input[type="date"]::-webkit-calendar-picker-indicator,
        input[type="time"]::-webkit-calendar-picker-indicator { filter: invert(0.6); cursor: pointer; }
        input::placeholder { color: rgba(234,224,207,0.3); }
        input:focus { border-color: rgba(114,136,174,0.65) !important; }
        @keyframes dropIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeUp  { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes float   { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-8px); } }
        .app-wrap { animation: fadeUp 0.45s ease; }
        .swap-btn:hover { background: rgba(114,136,174,0.28) !important; border-color: rgba(114,136,174,0.6) !important; }
        .swap-btn:active { transform: scale(0.92) rotate(180deg) !important; }
        .dt-input {
          background: rgba(75,86,148,0.18);
          border: 1.5px solid rgba(114,136,174,0.3);
          border-radius: 10px; padding: 10px 10px;
          color: #EAE0CF; font-size: 13px;
          outline: none; font-family: DM Sans, sans-serif;
          box-sizing: border-box;
          color-scheme: dark; min-width: 0;
        }
        .dt-input:focus { border-color: rgba(114,136,174,0.65); }
      `}</style>

      <div style={{
        minHeight: "100vh", background: "#111844",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "32px 16px 48px", position: "relative", overflow: "hidden",
      }}>
        {/* Ambient orbs */}
        {[
          { top:"6%", left:"10%", w:340, a:"float 8s ease-in-out infinite", color:"rgba(75,86,148,0.22)" },
          { bottom:"8%", right:"6%", w:260, a:"float 10s ease-in-out infinite reverse", color:"rgba(114,136,174,0.16)" },
          { top:"42%", right:"22%", w:160, a:"float 13s ease-in-out infinite", color:"rgba(234,224,207,0.05)" },
        ].map((o, i) => (
          <div key={i} style={{
            position: "fixed", borderRadius: "50%", pointerEvents: "none",
            width: o.w, height: o.w,
            top: o.top, left: o.left, bottom: o.bottom, right: o.right,
            background: `radial-gradient(circle, ${o.color} 0%, transparent 70%)`,
            animation: o.a,
          }}/>
        ))}

        <div className="app-wrap" style={{ width:"100%", maxWidth:480, display:"flex", flexDirection:"column", gap:20 }}>

          {/* Header */}
          <div style={{ textAlign:"center", paddingBottom:2 }}>
            <div style={{ display:"inline-flex", alignItems:"center", gap:10, marginBottom:6 }}>
              <div style={{
                width:32, height:32, borderRadius:10,
                background:"rgba(75,86,148,0.4)", border:"1px solid rgba(114,136,174,0.4)",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="#EAE0CF" strokeWidth="2"/>
                  <path d="M12 7v5l3 3" stroke="#EAE0CF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span style={{ fontSize:26, fontWeight:700, color:"#EAE0CF", letterSpacing:"-0.03em" }}>Timely</span>
            </div>
            <p style={{ fontSize:12, color:"rgba(234,224,207,0.38)", letterSpacing:"0.02em" }}>
              Real-time timezone converter
            </p>
          </div>

          {/* Main card */}
          <div style={{
            background:"rgba(17,24,68,0.72)", border:"1.5px solid rgba(114,136,174,0.22)",
            borderRadius:24, padding:"24px 20px",
            backdropFilter:"blur(20px)", display:"flex", flexDirection:"column", gap:18,
          }}>
            {/* City selectors */}
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <div>
                <div style={{ fontSize:10, letterSpacing:"0.09em", color:"rgba(234,224,207,0.38)", textTransform:"uppercase", marginBottom:7, paddingLeft:2 }}>From</div>
                <CityDropdown value={city1} onChange={setCity1} exclude={city2}/>
              </div>

              {/* Swap */}
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ flex:1, height:1, background:"rgba(114,136,174,0.15)" }}/>
                <button className="swap-btn" onClick={handleSwap} title="Swap cities" style={{
                  display:"flex", alignItems:"center", justifyContent:"center",
                  width:34, height:34, borderRadius:9,
                  background:"rgba(75,86,148,0.16)", border:"1.5px solid rgba(114,136,174,0.28)",
                  cursor:"pointer", color:"rgba(234,224,207,0.55)",
                  transition:"background 0.2s, border-color 0.2s, transform 0.3s", flexShrink:0,
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <div style={{ flex:1, height:1, background:"rgba(114,136,174,0.15)" }}/>
              </div>

              <div>
                <div style={{ fontSize:10, letterSpacing:"0.09em", color:"rgba(234,224,207,0.38)", textTransform:"uppercase", marginBottom:7, paddingLeft:2 }}>To</div>
                <CityDropdown value={city2} onChange={setCity2} exclude={city1}/>
              </div>
            </div>

            <div style={{ height:1, background:"rgba(114,136,174,0.12)" }}/>

            {/* Live clocks */}
            {city1 && city2 ? (
              <>
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  <TimeDisplay city={city1} primary={true}/>
                  <TimeDisplay city={city2} primary={false}/>
                </div>
                <div style={{
                  background:"rgba(75,86,148,0.1)", borderRadius:11,
                  padding:"10px 14px", border:"1px solid rgba(114,136,174,0.16)",
                }}>
                  <TimeDiff city1={city1} city2={city2}/>
                </div>
              </>
            ) : (
              <div style={{ textAlign:"center", padding:"28px 16px", color:"rgba(234,224,207,0.28)", fontSize:13 }}>
                Select two cities to compare their times
              </div>
            )}
          </div>

          {/* Custom converter */}
          <CustomConverter city1={city1} city2={city2}/>

          {/* Footer */}
          <div style={{ textAlign:"center", fontSize:11, color:"rgba(234,224,207,0.18)", letterSpacing:"0.04em" }}>
            Live clocks update every second · {CITIES.length} cities
          </div>
        </div>
      </div>
    </>
  );
}